import React, { useCallback, useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import qs from 'query-string';

import { useCheckEmailAvailabilityMutation } from 'api/main';
import { texts } from 'utils/languages/fr';

import { ActionButton, CancelButton, CustomDialogTitle, CustomTextField } from 'components';
import { Dialog, DialogActions, DialogContent, Typography } from '@mui/material';
import { validateSchema } from 'utils/helpers';
import { emailSchema } from 'containers/communication/EditableEmailAddress';
import { emailAvailabilityStatus, queryStringifyOptions } from 'utils/constants';

const CreateSubscriptionDialog = ({ onClose, open, fundId, onExternalFlow }) => {
    const dispatch = useDispatch();
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [checkEmail, { isLoading: loading, isSuccess: checkSuccess, data: response, reset }] =
        useCheckEmailAvailabilityMutation();

    const handleChange = useCallback(e => {
        setEmail(e.target.value?.trim());
    }, []);

    const handleSubmit = useCallback(() => {
        setError('');
        const { valid } = validateSchema(emailSchema, email);
        if (valid) {
            checkEmail(email);
        } else {
            setError(texts.ADRESSE_EMAIL_SAISIE_PAS_VALIDE);
        }
    }, [checkEmail, email]);

    const handleKeyDown = useCallback(
        e => {
            if (e.key === 'Enter') {
                handleSubmit();
            }
        },
        [handleSubmit]
    );

    useEffect(() => {
        if (checkSuccess) {
            if (response.status === emailAvailabilityStatus.AVAILABLE) {
                const queryObj = { email };
                if (fundId) {
                    // a product card was used to get here
                    // there's no user with the submitted email
                    queryObj.product = fundId;
                    onExternalFlow({ email });
                } else {
                    // the process was initiated on the subscriptions page
                    // the fund will be selected in the tunnel entry UI
                    dispatch(
                        push(
                            `/create-subscription?${qs.stringify(queryObj, queryStringifyOptions)}`
                        )
                    );
                }
            } else if (response.status === emailAvailabilityStatus.WITH_FINISHED_SUBSCRIPTION) {
                const queryObj = { userId: response.userId };
                if (fundId) {
                    // we know user ID and fund ID so we can already create the new indirect subscription
                    queryObj.product = fundId;
                    onExternalFlow({ userId: response.userId });
                } else {
                    dispatch(
                        push(
                            `/add-indirect-subscription?${qs.stringify(
                                queryObj,
                                queryStringifyOptions
                            )}`
                        )
                    );
                }
            } else {
                setError(texts.EMAIL_UNAVAILABLE);
            }
        }
    }, [checkSuccess, dispatch, response, email, fundId, onExternalFlow]);

    useEffect(() => {
        if (!open) {
            setEmail('');
            setError('');
            reset();
        }
    }, [open, reset]);

    return (
        <Dialog open={open}>
            <CustomDialogTitle title={texts.CREATE_SUBSCRIPTION} handleClose={onClose} />
            <DialogContent onKeyDown={handleKeyDown}>
                <CustomTextField
                    value={email}
                    label={texts.EMAIL_SUBSCRIBER}
                    onChange={handleChange}
                    error={error}
                />
                <Typography variant="body2" color="textSecondary" gutterBottom>
                    {texts.EMAIL_INFO_1}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                    {texts.EMAIL_INFO_2}
                </Typography>
            </DialogContent>
            <DialogActions>
                <CancelButton disabled={loading} onClick={onClose}>
                    {texts.CANCEL}
                </CancelButton>
                <ActionButton onClick={handleSubmit} loading={loading}>
                    {texts.CONFIRMER_CREATE_SUBSCRIPTION}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

CreateSubscriptionDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onExternalFlow: PropTypes.func,
    fundId: PropTypes.number,
};

export default CreateSubscriptionDialog;
